import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineArrowLeft,
  HiOutlineDocumentArrowDown,
  HiOutlineExclamationTriangle,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
  HiOutlineArrowPath,
  HiOutlineCalendar,
} from 'react-icons/hi2';
import pmStandardService from '../services/pmStandardService';
import plantService from '../services/plantService';
import lineService from '../services/lineService';
import machineService from '../services/machineService';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  showReportGeneratingToast,
  reportGenerationDone,
  reportGenerationFailed,
} from '../utils/reportGenerationToast';
import './Reports.css';

const PmOverdueReport = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  
  // Filter states
  const [plants, setPlants] = useState([]);
  const [allLines, setAllLines] = useState([]);
  const [allMachines, setAllMachines] = useState([]);
  
  const [selectedPlants, setSelectedPlants] = useState([]);
  const [selectedLines, setSelectedLines] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [reportGenerated, setReportGenerated] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const pData = await plantService.getAllPlants();
      setPlants(pData || []);
      
      const lData = await lineService.getAllLines();
      setAllLines(lData || []);

      const mData = await machineService.getAllMachines();
      setAllMachines(mData || []);

      // Auto-select plant if only 1 exists
      if (pData?.length === 1) {
        const pId = pData[0].plantId;
        setSelectedPlants([pId]);
      }

      // Auto-select all lines ONLY for single-plant users
      if (pData?.length === 1 && selectedLines.length === 0) {
        const pId = pData[0].plantId;
        const plantLines = (lData || []).filter(l => l.plantId === pId).map(l => l.lineId);
        if (plantLines.length > 0) {
          setSelectedLines(plantLines);
        }
      }
      
      // Auto-fetch initial report (Removed to comply with user request)
      // fetchData();
    } catch (err) {
      console.error('Error loading filter data:', err);
    }
  };

  const fetchData = async () => {
    showReportGeneratingToast();
    try {
      setLoading(true);
      const res = await pmStandardService.getPmOverdueReport(selectedPlants, selectedLines, selectedMachines);
      setData(res || []);
      setReportGenerated(true);
      reportGenerationDone();
    } catch (err) {
      console.error('Error fetching overdue report:', err);
      reportGenerationFailed('Could not load overdue report.');
    } finally {
      setLoading(false);
    }
  };

  const lineOptions = selectedPlants.length > 0
    ? allLines.filter(l => selectedPlants.includes(l.plantId))
    : allLines;

  const machineOptions = selectedLines.length > 0
    ? allMachines.filter(m => selectedLines.includes(m.lineId))
    : selectedPlants.length > 0
      ? allMachines.filter(m => lineOptions.some(l => l.lineId === m.lineId))
      : allMachines;

  const handlePlantChange = (vals) => {
    setSelectedPlants(vals);
    const validLines = allLines.filter(l => vals.includes(l.plantId)).map(l => l.lineId);
    setSelectedLines(prev => prev.filter(id => validLines.includes(id)));
    
    // Also reset machines that no longer belong to selected plants
    const validMachines = allMachines.filter(m => validLines.includes(m.lineId)).map(m => m.machineId);
    setSelectedMachines(prev => prev.filter(id => validMachines.includes(id)));
  };

  const handleLineChange = (vals) => {
    setSelectedLines(vals);
    const validMachines = allMachines.filter(m => vals.includes(m.lineId)).map(m => m.machineId);
    setSelectedMachines(prev => prev.filter(id => validMachines.includes(id)));
  };

  const handleMachineChange = (vals) => {
    setSelectedMachines(vals);
  };

  const exportToExcel = () => {
    if (data.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(
      data.map((item) => ({
        'Plan Name': item.planName,
        Machine: item.machineName,
        'Machine Code': item.machineCode,
        Plant: item.plantName,
        'Due Date': new Date(item.dueDate).toLocaleDateString(),
        'Days Overdue': item.daysOverdue,
        Priority: item.priority,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Overdue PMs');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    saveAs(dataBlob, `PM_Overdue_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const summary = [
    {
      label: 'Total Overdue',
      value: data.length,
      sub: 'Tasks past due date',
      color: '#ef4444',
      borderColor: '#ef4444',
    },
    {
      label: 'Avg. Days Overdue',
      value:
        data.length > 0
          ? (data.reduce((acc, curr) => acc + curr.daysOverdue, 0) / data.length).toFixed(1)
          : 0,
      sub: 'Average overdue duration',
      color: '#f59e0b',
      borderColor: '#f59e0b',
    },
    {
      label: 'High / Critical',
      value: data.filter((d) => ['High', 'Critical'].includes(d.priority)).length,
      sub: 'Urgent items requiring action',
      color: '#dc2626',
      borderColor: '#dc2626',
    },
    {
      label: 'Plants Affected',
      value: new Set(data.map((d) => d.plantName)).size,
      sub: 'Across all plant locations',
      color: '#8b5cf6',
      borderColor: '#8b5cf6',
    },
  ];

  return (
    <div className="pagecontainer report-page">
      {/* ── Page Header ── */}
      <div className="page-header page-header--report-tight">
        <div className="oee-report-header-left">
          <button
            className="oee-report-back-btn"
            onClick={() => navigate('/home/reports')}
            title="Back to Reports"
          >
            <HiOutlineArrowLeft size={18} />
          </button>
          <div>
            <h1 className="oee-report-title">
              <HiOutlineExclamationTriangle /> PM Overdue Report
            </h1>
            <p className="oee-report-subtitle">
              Detailed list of all overdue planned maintenance tasks across the plant
            </p>
          </div>
        </div>
        <div className="header-actions">
           <button className="oee-filter-toggle-btn" onClick={() => setShowFilters(p => !p)}>
            {showFilters ? <HiOutlineChevronUp /> : <HiOutlineChevronDown />} Filters
          </button>
          <button className="btn-secondary" onClick={fetchData} disabled={loading}>
            <HiOutlineArrowPath />
            Refresh
          </button>
          <button className="btn-primary" onClick={exportToExcel} disabled={data.length === 0}>
            <HiOutlineDocumentArrowDown />
            Export
          </button>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      {showFilters && (
        <div className="reportbox searchfilterbox">
          <div className="report-filters">
            <div className="filter-group">
              <label>Plants</label>
              <MultiSelectDropdown
                options={plants}
                selectedValues={selectedPlants}
                onChange={handlePlantChange}
                placeholder="All Plants"
                displayKey="plantName"
                valueKey="plantId"
              />
            </div>
            <div className="filter-group">
              <label>LINE</label>
              <MultiSelectDropdown
                options={lineOptions}
                selectedValues={selectedLines}
                onChange={handleLineChange}
                placeholder="All Lines"
                displayKey="lineName"
                valueKey="lineId"
                disabled={selectedPlants.length === 0}
              />
            </div>
            <div className="filter-group">
              <label>Machines</label>
              <MultiSelectDropdown
                options={machineOptions}
                selectedValues={selectedMachines}
                onChange={handleMachineChange}
                placeholder="All Machines"
                displayKey="machineName"
                valueKey="machineId"
              />
            </div>
          </div>

          <div className="report-filter-actions">
            {(selectedPlants.length > 0 || selectedLines.length > 0 || selectedMachines.length > 0) && (
              <button 
                className="oee-clear-filters-btn" 
                onClick={() => {
                  setSelectedPlants([]);
                  setSelectedLines([]);
                  setSelectedMachines([]);
                  setData([]);
                  setReportGenerated(false);
                }}
              >
                ✕ Clear Filters
              </button>
            )}
            <button type="button" className="btn-primary" onClick={fetchData} disabled={loading}>
              <HiOutlineCalendar /> Generate Report
            </button>
          </div>
        </div>
      )}

      {/* ── KPI Cards ── */}
      {reportGenerated && (
        <div className="report-kpi-grid">
          {summary.map((s) => (
            <div
              key={s.label}
              className="report-card report-card--pad-lg report-card--accent-left"
              style={{ '--report-accent': s.borderColor }}
            >
              <p className="report-card__kpi-label">{s.label}</p>
              <p className="report-card__kpi-value" style={{ color: s.color }}>
                {s.value}
              </p>
              <p className="report-card__kpi-sub">{s.sub}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Table ── */}
      <div className="reports-table">
        <div className="table-header report-table-shell__head--split">
          <h3>Overdue Details</h3>
          {!loading && (
            <span className="report-table-shell__head-meta">
              {data.length} task{data.length !== 1 ? 's' : ''} overdue
            </span>
          )}
        </div>
        {loading && data.length === 0 ? (
          <div className="loading-container">Loading overdue tasks…</div>
        ) : (
          <div className="report-table-wrapper" style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
            <table className="report-table">
              <thead>
                <tr>
                  <th>Plan / Standard</th>
                  <th>Machine</th>
                  <th>Plant</th>
                  <th>Due Date</th>
                  <th>Days Overdue</th>
                  <th>Priority</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <tr key={idx}>
                    <td>
                      <strong>{row.planName}</strong>
                    </td>
                    <td>
                      {row.machineName}{' '}
                      <span className="report-table-shell__head-meta" style={{ fontSize: '0.85rem' }}>
                        ({row.machineCode})
                      </span>
                    </td>
                    <td>{row.plantName}</td>
                    <td>{new Date(row.dueDate).toLocaleDateString()}</td>
                    <td className="text-danger">
                      <strong>{row.daysOverdue}</strong>
                    </td>
                    <td>
                      <span className={`priority-tag ${row.priority?.toLowerCase() || 'medium'}`}>
                        {row.priority || 'Medium'}
                      </span>
                    </td>
                  </tr>
                ))}
                {data.length === 0 && !loading && (
                  <tr>
                    <td colSpan="6" className="no-data">
                      {reportGenerated ? "No overdue tasks found" : "Select filters and click 'Generate Report' to view results."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PmOverdueReport;
